import {
  TextField,
  Box,
  Dialog,
  Button,
  useTheme,
  Theme,
  useMediaQuery,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import {
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon,
} from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'
import { RolesContext } from 'src/context/Roles'
import { GlobalContext } from 'src/context/global'
import {
  LoadingBtn,
  StyleSelectMultiple,
  StyleSelectOne,
  AdaptiveTooltip,
} from 'src/components'

const getUseStyles = (theme: Theme) =>
  makeStyles(() => {
    return {
      formInputSearch: {
        width: '100%',
        '& .MuiInputBase-input': {
          color: theme.palette.text.secondary,
        },
      },
      backButton: {
        boxSizing: 'border-box',
        width: '235.5px',
        height: '40px',
        lineHeight: '40px',
        fontSize: '14px',
        fontWeight: 600,
        textAlign: 'center',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.custom.dialogButton}`,
        color: theme.palette.custom.dialogButton,
        cursor: 'pointer',
        marginRight: '24px',
      },
      footerButton: {
        width: '235.5px !important',
        height: '40px !important',
      },
      pageDefaultButton: {
        background:
          theme.palette.mode === 'light'
            ? 'rgba(16, 18, 20, 0.05)'
            : 'rgba(255, 255, 255, 0.05)',
        border: `1px solid ${
          theme.palette.mode === 'light'
            ? 'rgba(16, 18, 20, 0.3)'
            : 'rgba(255, 255, 255, 0.6)'
        }`,
        color: theme.palette.mode === 'light' ? '#828282' : '#FFF',
      },
      pageWriteButton: {
        background: 'rgba(39, 174, 96, 0.1)',
        border: '1px solid rgba(39, 174, 96, 0.5)',
        color: '#27AE60',
      },
      pageReadButton: {
        background: 'rgba(255, 142, 0, 0.1)',
        border: '1px solid rgba(255, 142, 0, 0.5)',
        color:
          theme.palette.mode === 'light'
            ? '#7487AA'
            : theme.palette.primary.main,
      },
      helpIcon: {
        fontSize: '16px !important',
        verticalAlign: 'middle',
        marginLeft: '4px',
        marginTop: '-2px',
        cursor: 'pointer',
      },
      dialog: {
        boxSizing: 'border-box',
        padding: '40px',
        maxWidth: '575px',
        background: theme.palette.custom.dialogBg,
      },
      title: {
        position: 'relative',
        fontSize: '16px',
        fontWeight: 600,
      },
      closeFilterIcon: {
        position: 'absolute',
        fontSize: '24px !important',
        right: '3px',
        cursor: 'pointer',
      },
      formLabel: {
        fontSize: '12px',
        color: theme.palette.custom.inputLabel,
        marginBottom: '8px',
      },
      selectRole: {
        width: '100% !important',
      },
    }
  })

const DialogView = () => {
  const theme: Theme = useTheme()
  const useStyles = getUseStyles(theme)
  const classes = useStyles()
  const [t] = useTranslation()
  const min638px = useMediaQuery('(min-width:638px)')
  const { state, dispatch, editAccount } = useContext(RolesContext)
  const { state: globalState } = useContext(GlobalContext)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(0)
  const [gameList, setGameList] = useState([])
  const handleClose = () => {
    dispatch({
      type: 'editDialogOpen',
      payload: false,
    })
  }
  const inputChange = (e: any) => {
    setEmail(e.target.value)
  }
  const selectGame = (row: any) => {
    setGameList(row)
  }
  const saveAccount = () => {
    const data = { ...state.dialogData, role_id: role }
    if (state.limitAppViewList.includes(role)) {
      data.app_keys = gameList.map((item: any) => item.app_key)
    }
    editAccount(data)
  }
  useEffect(() => {
    if (state.editDialogOpen) {
      setEmail(state.dialogData.email)
      setRole(state.dialogData.role_id || '')
      if (state.dialogData?.app_keys?.length) {
        const temp: any = globalState.gameOption.filter((item: any) =>
          state.dialogData.app_keys.includes(item.app_key)
        )
        setGameList(temp)
      } else {
        setGameList([])
      }
    }
    // eslint-disable-next-line
  }, [state.editDialogOpen, globalState.gameOption])

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={state.editDialogOpen}
    >
      <Box className={classes.dialog}>
        <Box className={classes.title}>
          Edit User
          <CloseIcon
            className={classes.closeFilterIcon}
            onClick={handleClose}
          />
        </Box>
        <Box className={classes.formLabel} marginTop="24px">
          Email:
        </Box>
        <TextField
          className={classes.formInputSearch}
          name="email"
          placeholder="Email"
          value={email}
          onChange={inputChange}
          variant="outlined"
          disabled={true}
        />
        <Box className={classes.formLabel} marginTop="24px">
          Role:
        </Box>
        <StyleSelectOne
          placeholder="Role"
          value={role}
          labelKey="title"
          valueKey="id"
          selectStyle={true}
          customClass={classes.selectRole}
          onChange={(value: any) => {
            setRole(value)
          }}
          hideOption={true}
          options={state.allRolesList}
        />
        {state.limitAppViewList.includes(role) && (
          <>
            <Box className={classes.formLabel} marginTop="24px">
              Select Games:
            </Box>
            <StyleSelectMultiple
              customClass={classes.selectRole}
              label=""
              placeholder="Select Games"
              value={gameList}
              labelKey="name"
              valueKey="app_key"
              selectStyle={true}
              onChange={selectGame}
              options={globalState.gameOption}
            />
          </>
        )}
        <Box
          display="flex"
          flexWrap="wrap"
          boxSizing="border-box"
          sx={{
            justifyContent: min638px ? 'flex-start' : 'space-around',
          }}
        >
          {Object.keys(
            state.rolesListObject[role]?.permission || state.noneRoleObject
          ).map((item: string, index: number) => (
            <Box
              key={item}
              display="inline-block"
              borderRadius="6px"
              textAlign="center"
              minWidth="157px"
              height="30px"
              lineHeight="30px"
              fontSize="12px"
              margin={index % 3 === 1 && min638px ? '0 8px' : '0'}
              marginTop="24px"
              className={
                state.rolesListObject[role]
                  ? state.rolesListObject[role]?.permission[item] === 2
                    ? classes.pageWriteButton
                    : state.rolesListObject[role]?.permission[item] === 1
                    ? classes.pageReadButton
                    : classes.pageDefaultButton
                  : classes.pageDefaultButton
              }
            >
              {t(item) || item}
              <AdaptiveTooltip
                isMobile={globalState.isMobile}
                title={
                  state.rolesListObject[role]
                    ? state.rolesListObject[role]?.permission[item] === 2
                      ? 'Write permission'
                      : state.rolesListObject[role]?.permission[item] === 1
                      ? 'Read-only access'
                      : 'No permission'
                    : 'No permission'
                }
                arrow
                placement="top"
                disableInteractive
              >
                <InfoOutlinedIcon className={classes.helpIcon} />
              </AdaptiveTooltip>
            </Box>
          ))}
        </Box>
        <Box display="flex" marginTop="24px">
          <Box className={classes.backButton} onClick={handleClose}>
            Back
          </Box>
          <Button
            className={classes.footerButton}
            color="primary"
            variant="contained"
            onClick={saveAccount}
          >
            {state.editDeviceLoading ? (
              <Box>
                <LoadingBtn
                  loadingStyle={{
                    justifyContent: 'center',
                  }}
                />
              </Box>
            ) : (
              'Save'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}

export default DialogView
